const ACCESS_KEY = 'mindplace:auth-access-token';
const USER_KEY = 'mindplace:auth-user';
const API_KEY = 'mindplace:sync-api-url';
const SESSION_HINT_KEY = 'mindplace:auth-session-hint';
const AUTH_SCOPE_EVENT_KEY = 'mindplace:auth-scope-event';

let accessToken = '';
let user = null;
let initialized = false;
let online = typeof navigator === 'undefined' || navigator.onLine !== false;
let refreshPromise = null;

function apiUrl() {
  try {
    return (
      localStorage.getItem(API_KEY) ||
      window.__MINDPLACE_SYNC_API__ ||
      '/api'
    );
  } catch (_) {
    return '/api';
  }
}

function emit(extra = {}) {
  try {
    window.dispatchEvent(
      new CustomEvent('mindplace:auth-state', {
        detail: {
          authenticated: Boolean(accessToken && user),
          user,
          online,
          ...extra
        }
      })
    );
  } catch (_) {}
}

function readCachedUser() {
  try {
    const raw = sessionStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
}

function cacheUser(next) {
  user = next || null;

  try {
    if (user) {
      sessionStorage.setItem(USER_KEY, JSON.stringify(user));
    } else {
      sessionStorage.removeItem(USER_KEY);
    }
  } catch (_) {}
}

function saveAccess(token) {
  accessToken = token || '';

  try {
    if (accessToken) {
      sessionStorage.setItem(ACCESS_KEY, accessToken);
    } else {
      sessionStorage.removeItem(ACCESS_KEY);
    }
  } catch (_) {}
}

function loadAccess() {
  try {
    return sessionStorage.getItem(ACCESS_KEY) || '';
  } catch (_) {
    return '';
  }
}

function hasSessionHint() {
  try {
    return localStorage.getItem(SESSION_HINT_KEY) === '1';
  } catch (_) {
    return false;
  }
}

function broadcastAuthScope() {
  try {
    localStorage.setItem(AUTH_SCOPE_EVENT_KEY, JSON.stringify({ at: Date.now(), userId: user?.id || null }));
  } catch (_) {}
}

function setSessionHint(value) {
  try {
    if (value) {
      localStorage.setItem(SESSION_HINT_KEY, '1');
    } else {
      localStorage.removeItem(SESSION_HINT_KEY);
    }
  } catch (_) {}
}

export function getAccessToken() {
  return accessToken;
}

export function getUser() {
  return user;
}

export function getApiUrl() {
  return apiUrl();
}

export function getAuthState() {
  return {
    authenticated: Boolean(accessToken && user),
    user,
    online
  };
}

async function parseResponse(response) {
  let body = null;

  try {
    body = await response.json();
  } catch (_) {}

  if (!response.ok) {
    const error = new Error(
      body?.message ||
        body?.error?.message ||
        `Error HTTP ${response.status}`
    );

    error.status = response.status;
    error.body = body;

    throw error;
  }

  return body;
}

async function request(path, options = {}, retry = true) {
  const headers = new Headers(options.headers || {});
  headers.set('Content-Type', 'application/json');

  if (accessToken) {
    headers.set('Authorization', `Bearer ${accessToken}`);
  }

  const response = await fetch(`${apiUrl()}${path}`, {
    ...options,
    headers,
    credentials: 'include'
  });

  if (
    response.status === 401 &&
    retry &&
    hasSessionHint() &&
    path !== '/auth/login' &&
    path !== '/auth/register' &&
    path !== '/auth/refresh' &&
    path !== '/auth/forgot-password' &&
    path !== '/auth/reset-password'
  ) {
    const refreshed = await refresh(false);

    if (refreshed) {
      return request(path, options, false);
    }
  }

  return parseResponse(response);
}

export async function refresh(throwOnError = false) {
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = (async () => {
    try {
      const response = await fetch(`${apiUrl()}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      if (!response.ok) {
        saveAccess('');
        cacheUser(null);

        if (response.status === 401) {
          setSessionHint(false);
        }

        initialized = true;
        emit();

        if (throwOnError && response.status !== 401) {
          await parseResponse(response);
        }

        return false;
      }

      const body = await response.json();

      saveAccess(body.accessToken);
      cacheUser(body.user);
      setSessionHint(true);
      broadcastAuthScope();

      initialized = true;
      emit();

      return true;
    } catch (error) {
      saveAccess('');
      cacheUser(null);
      initialized = true;
      emit();

      if (throwOnError) {
        throw error;
      }

      return false;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

export async function bootstrapAuth() {
  if (initialized) {
    return getAuthState();
  }

  accessToken = loadAccess();
  user = readCachedUser();

  /*
   * Si tenemos access token y usuario almacenados en esta sesión,
   * comprobamos que sigan siendo válidos.
   */
  if (accessToken && user) {
    try {
      const body = await request('/auth/me', {}, false);

      cacheUser(body.user);
      setSessionHint(true);
      broadcastAuthScope();

      initialized = true;
      emit();

      return getAuthState();
    } catch (_) {
      saveAccess('');
      cacheUser(null);
    }
  }

  /*
   * Solo intentamos refresh automático si sabemos que
   * anteriormente existió una sesión.
   *
   * Primera visita sin cuenta:
   * no hacemos POST /auth/refresh y no aparece un 401.
   */
  if (hasSessionHint()) {
    await refresh(false);
  }

  initialized = true;
  emit();

  return getAuthState();
}

export async function login(identifier, password) {
  const body = await request(
    '/auth/login',
    {
      method: 'POST',
      body: JSON.stringify({
        identifier,
        password
      })
    },
    false
  );

  saveAccess(body.accessToken);
  cacheUser(body.user);
  setSessionHint(true);
  broadcastAuthScope();

  initialized = true;
  emit();

  return body;
}

export async function register(username, email, password, name = '') {
  const body = await request(
    '/auth/register',
    {
      method: 'POST',
      body: JSON.stringify({
        username,
        email,
        password,
        name
      })
    },
    false
  );

  saveAccess(body.accessToken);
  cacheUser(body.user);
  setSessionHint(true);
  broadcastAuthScope();

  initialized = true;
  emit();

  return body;
}

export async function logout() {
  try {
    if (hasSessionHint()) {
      await request(
        '/auth/logout',
        {
          method: 'POST'
        },
        false
      );
    }
  } catch (_) {}

  saveAccess('');
  cacheUser(null);
  setSessionHint(false);
  broadcastAuthScope();

  initialized = true;
  emit();
}

export async function me() {
  const body = await request('/auth/me');

  cacheUser(body.user);
  setSessionHint(true);
  emit();

  return body.user;
}

export async function updateProfile(name) {
  const body = await request('/auth/me', {
    method: 'PATCH',
    body: JSON.stringify({ name })
  });

  cacheUser(body.user);
  setSessionHint(true);
  emit();

  return body.user;
}

export async function changePassword(currentPassword, newPassword) {
  const body = await request('/auth/change-password', {
    method: 'POST',
    body: JSON.stringify({
      currentPassword,
      newPassword
    })
  });

  saveAccess('');
  cacheUser(null);
  setSessionHint(false);
  broadcastAuthScope();
  emit();

  return body;
}

export async function forgotPassword(identifier) {
  return request(
    '/auth/forgot-password',
    {
      method: 'POST',
      body: JSON.stringify({ identifier })
    },
    false
  );
}

export async function resetPassword(token, password) {
  return request(
    '/auth/reset-password',
    {
      method: 'POST',
      body: JSON.stringify({
        token,
        password
      })
    },
    false
  );
}

window.addEventListener('online', () => {
  online = true;
  emit({ online: true });
});

window.addEventListener('offline', () => {
  online = false;
  emit({ online: false });
});

export function installAuthClient() {
  window.__mindplaceAuth = {
    bootstrapAuth,
    login,
    register,
    logout,
    me,
    updateProfile,
    changePassword,
    forgotPassword,
    resetPassword,
    getAccessToken,
    getUser,
    getApiUrl,
    getAuthState,
    refresh
  };

  void bootstrapAuth();

  return window.__mindplaceAuth;
}